<script setup lang="ts">
import { blogCtaTarget } from "~/utils/blogCtaTarget";
import { isCollectiveAuthor, slugifyAuthor } from "#shared/utils/blogAuthors";
import { BLOG_CANONICAL_OVERRIDES } from "#shared/utils/blogCanonicalOverrides";
import { truncateForMeta } from "@donoor/utils/string";
import { positionVideo, VIDEOS_ARTICLES } from "~/utils/blogVideosFormation";

interface TocLink {
  id: string;
  text: string;
  level: number;
}

definePageMeta({
  key: (route) => route.fullPath,
});

const route = useRoute();
const slug = (route.params as { slug: string }).slug;

const { fetchArticle, fetchArticles, getImageUrl, getAvatarUrl, onImageError, formatDate } = useBlog();
const { sanitize } = useSanitizeHtml();

const { data: article, error } = await fetchArticle(slug);

if (error.value || !article.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Article non trouvé",
  });
}

// Get related articles (same category, excluding current)
const { data: relatedData } = await fetchArticles({
  category: article.value.category?.slug,
  limit: 4,
});

const relatedArticles = computed(() => {
  if (!relatedData.value?.articles) return [];
  return relatedData.value.articles
    .filter((a) => a.slug !== slug)
    .slice(0, 3);
});

// Extract table of contents from content
const tocLinks = computed<TocLink[]>(() => {
  if (!article.value?.content) return [];
  const links: TocLink[] = [];
  const regex = /<h([23])[^>]*>([^<]+)<\/h[23]>/gi;
  let match;
  while ((match = regex.exec(article.value.content)) !== null) {
    const level = parseInt(match[1] ?? "2");
    const text = (match[2] ?? "").trim();
    const id = text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (text) {
      links.push({ id, text, level });
    }
  }
  return links;
});

// Process content to add IDs to headings
const processedContent = computed(() => {
  if (!article.value?.content) return "";
  let content = article.value.content;
  tocLinks.value.forEach((link) => {
    const regex = new RegExp(
      `<h${link.level}>\\s*${link.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*</h${link.level}>`,
      "gi"
    );
    content = content.replace(
      regex,
      `<h${link.level} id="${link.id}">${link.text}</h${link.level}>`
    );
  });
  // Les 131 photos des articles (Directus) ont toutes `alt=""` : pour Google
  // Images et les lecteurs d'écran elles n'existaient pas (audit SEO
  // 2026-10-18). Une photo sans texte reçoit le titre de l'article.
  const titre = (article.value.title || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  if (titre) content = content.replace(/(<img\b[^>]*?\salt=)(["'])\2/gi, `$1"${titre}"`);
  return content;
});

/**
 * Découpe l'article en DEUX pour intercaler un appel à l'action contextuel.
 *
 * POURQUOI : mesuré sur 60 jours, 591 sessions arrivent sur un article et
 * repartent sans voir une autre page. L'appel à l'action ne vivait qu'en FIN
 * d'article — là où presque personne n'arrive. Un bloc placé au milieu est vu
 * par ceux qui lisent vraiment.
 *
 * OÙ COUPER : à une frontière de `<h2>`, jamais ailleurs. Les `<h2>` sont
 * toujours de premier niveau dans le contenu Directus, donc couper là ne peut
 * pas scinder une phrase, un tableau ni une liste. On vise le premier `<h2>`
 * situé après la moitié du texte, et on exclut la FAQ finale — un encadré
 * juste avant « Questions fréquentes » tomberait au même endroit que celui de
 * fin d'article.
 *
 * Si aucune coupe sûre n'existe (article court, sans `<h2>`), on ne coupe pas :
 * `contenuApres` reste vide et le bloc du milieu n'est pas rendu. Jamais de
 * découpe approximative — un HTML cassé vaut bien pire qu'un appel manquant.
 */
const POSITIONS_COUPE = computed(() => {
  const html = sanitize(processedContent.value);
  if (!html) return { avant: "", apres: "" };

  const titres = [...html.matchAll(/<h2[\s>]/gi)].map((m) => m.index ?? -1).filter((i) => i >= 0);
  if (titres.length < 2) return { avant: html, apres: "" };

  // La FAQ de fin n'est pas un point de coupe acceptable.
  const debutFaq = html.search(/<h2[^>]*>\s*(FAQ|Questions fréquentes)/i);
  const candidats = titres.filter((i) => (debutFaq === -1 || i < debutFaq) && i > 0);
  if (candidats.length === 0) return { avant: html, apres: "" };

  const milieu = (debutFaq === -1 ? html.length : debutFaq) / 2;
  const coupe = candidats.find((i) => i >= milieu) ?? candidats[candidats.length - 1]!;

  // Trop près d'un bord : le bloc ferait doublon avec celui de fin d'article.
  if (coupe < html.length * 0.2 || coupe > html.length * 0.85) {
    return { avant: html, apres: "" };
  }
  return { avant: html.slice(0, coupe), apres: html.slice(coupe) };
});

// Vidéo de formation glissée dans l'article (utils/blogVideosFormation.ts) :
// la partie qui contient l'intertitre visé est coupée en deux, la vidéo se
// place juste avant ce `<h2>`. Intertitre introuvable = pas de vidéo.
const videoArticle = computed(() => (article.value?.slug ? VIDEOS_ARTICLES[article.value.slug] ?? null : null));
const couperPourVideo = (html: string) => {
  const video = videoArticle.value;
  if (!video || !html) return null;
  const i = positionVideo(html, video);
  return i > 0 ? { avant: html.slice(0, i), apres: html.slice(i) } : null;
};
const SEGMENTS_VIDEO = computed(() => ({
  avant: couperPourVideo(POSITIONS_COUPE.value.avant),
  apres: couperPourVideo(POSITIONS_COUPE.value.apres),
}));

// Scroll to section
const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// SEO
// Anti-cannibalisation (audit GSC 2026-07-25) : quand deux articles se battent
// sur la même requête, le plus faible pointe canoniquement vers le plus fort
// pour consolider le classement au lieu de le diluer. Source unique partagée
// avec les générateurs sitemap/RSS/Atom (qui EXCLUENT ces slugs) :
// shared/utils/blogCanonicalOverrides.ts.
const canonicalUrl =
  BLOG_CANONICAL_OVERRIDES[slug] ?? `https://donoor.org/blog/${slug}`;
const articleImage = computed(() => getImageUrl(article.value?.image, 1200));

// ─── Auteur RÉEL de l'article ────────────────────────────────────────────
// Le slug est dérivé du nom par la même fonction que la page auteur et que le
// sitemap (#shared/utils/blogAuthors) : une seule règle, donc jamais d'URL
// générée qui ne se résout pas.
const authorPagePath = computed(() =>
  article.value?.author?.name
    ? `/blog/auteur/${slugifyAuthor(article.value.author.name)}`
    : "/blog",
);
const authorPageUrl = computed(() => `https://donoor.org${authorPagePath.value}`);
const authorIsCollective = computed(() =>
  isCollectiveAuthor(article.value?.author?.name ?? ""),
);

// Description pour Google : 155 caractères au plus, coupée au mot (audit SEO
// 2026-10-18 : des résumés de 180 à 250 caractères étaient tronqués dans les
// résultats). Le résumé affiché sur le site reste entier.
const metaDescription = truncateForMeta(article.value.description || "", 155);

useSeoMeta({
  // Titre nu = 100 % du poids sur les mots-clés de l'article (pas de marque).
  title: article.value.title,
  description: metaDescription,
  ogTitle: article.value.title,
  ogDescription: metaDescription,
  ogImage: articleImage.value,
  ogType: "article",
  ogUrl: canonicalUrl,
  twitterCard: "summary_large_image",
  twitterTitle: article.value.title,
  twitterDescription: metaDescription,
  twitterImage: articleImage.value,
});

// Word count
const wordCount = computed(() => {
  if (!article.value?.content) return 0;
  const textContent = article.value.content.replace(/<[^>]*>/g, " ");
  return textContent.split(/\s+/).filter((word) => word.length > 0).length;
});

// FAQ extraction : tout <h3>question?</h3> + <p>réponse</p> situé après un
// <h2>FAQ ...</h2> ou <h2>Questions fréquentes ...</h2>. Active le rich
// snippet FAQ dans la SERP (jusqu'à 4 questions affichées dépliables).
const faqItems = computed<{ question: string; answer: string }[]>(() => {
  if (!article.value?.content) return [];
  const content = article.value.content;
  const faqStart = content.search(/<h2[^>]*>\s*(FAQ|Questions fréquentes)/i);
  if (faqStart === -1) return [];
  const faqEnd = content.indexOf("<h2", faqStart + 4);
  const faqBlock = content.slice(faqStart, faqEnd === -1 ? undefined : faqEnd);
  const items: { question: string; answer: string }[] = [];
  const re = /<h3[^>]*>([\s\S]*?)<\/h3>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;
  let m;
  while ((m = re.exec(faqBlock)) !== null) {
    const q = (m[1] ?? "").replace(/<[^>]+>/g, "").trim();
    const a = (m[2] ?? "").replace(/<[^>]+>/g, "").trim();
    if (q && a) items.push({ question: q, answer: a });
  }
  return items;
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  script: [
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        // BlogPosting (sous-type d'Article) : sémantiquement exact pour un
        // article de blog, même éligibilité rich results (audit SEO 2026-07-11).
        "@type": "BlogPosting",
        headline: article.value.title,
        description: article.value.description,
        image: articleImage.value,
        datePublished: article.value.date_published,
        dateModified: article.value.date_modified || article.value.date_published,
        inLanguage: "fr-FR",
        wordCount: wordCount.value,
        // E-E-A-T : Person enrichi (URL, worksFor, image, bio) — Google note
        // l'expertise de l'auteur, particulièrement pour les sujets YMYL
        // (Your Money Your Life : finance/dons défiscalisés ici).
        author: {
          // Person ou Organization selon l'auteur RÉEL.
          // AVANT (bug relevé le 2026-08-06) : `@type: "Person"` et
          // `@id: .../equipe/younes-miloud#person` étaient codés EN DUR sur
          // TOUS les articles — y compris ceux signés « Équipe Donoor ». Le
          // balisage affirmait donc à Google que chaque article du blog était
          // écrit par la même personne, et qu'un collectif était une personne.
          // Un balisage faux est pire que pas de balisage : Google le détecte
          // et dévalue l'ensemble des données structurées de la page.
          "@type": authorIsCollective.value ? "Organization" : "Person",
          // @id = l'entité de la page auteur : permet à Google de consolider
          // tous les articles d'un même auteur vers une entité unique
          // (E-E-A-T, audit SEO 2026-07-11).
          "@id": `${authorPageUrl.value}#author`,
          name: article.value.author?.name,
          ...(authorIsCollective.value
            ? {}
            : { jobTitle: article.value.author?.role }),
          ...(article.value.author?.bio ? { description: article.value.author.bio } : {}),
          ...(article.value.author?.avatar
            ? { image: getAvatarUrl(article.value.author.avatar) }
            : {}),
          // `worksFor` n'existe QUE sur Person dans le vocabulaire Schema.org.
          // Émise sur une Organization (auteur collectif « Équipe Donoor »),
          // elle rend le bloc invalide — 4 articles remontés en erreur par
          // SemRush le 2026-08-07. Pour le collectif l'entité EST Donoor :
          // `logo` (valide sur Organization) remplace `worksFor`.
          ...(authorIsCollective.value
            ? { logo: "https://donoor.org/icons/icon-512x512.png" }
            : {
                worksFor: {
                  "@type": "Organization",
                  name: "Donoor",
                  url: "https://donoor.org",
                  logo: "https://donoor.org/icons/icon-512x512.png",
                },
              }),
          // Page auteur dédiée (E-E-A-T) — avant : /blog (liste), signal
          // d'entité auteur flou pour Google (audit SEO 2026-07-11).
          url: authorPageUrl.value,
        },
        publisher: {
          "@type": "Organization",
          name: "Donoor",
          logo: {
            "@type": "ImageObject",
            url: "https://donoor.org/icons/icon-512x512.png",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
      }),
    },
    // BreadcrumbList : Accueil → Blog → Catégorie → Article (Karim 2026-06-15).
    // Active le rich snippet Breadcrumb dans la SERP (chemin lisible au-dessus
    // du title), améliore le CTR ~30% selon Google.
    {
      type: "application/ld+json" as const,
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://donoor.org" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://donoor.org/blog" },
          ...(article.value.category
            ? [{
                "@type": "ListItem",
                position: 3,
                name: article.value.category.name,
                item: `https://donoor.org/blog/categorie/${article.value.category.slug}`,
              }, {
                "@type": "ListItem",
                position: 4,
                name: article.value.title,
                item: canonicalUrl,
              }]
            : [{
                "@type": "ListItem",
                position: 3,
                name: article.value.title,
                item: canonicalUrl,
              }]),
        ],
      }),
    },
    // FAQPage : injecté seulement si l'article a une section FAQ détectée.
    // Active le rich snippet FAQ (4 questions max affichées dans la SERP).
    ...(faqItems.value.length > 0
      ? [{
          type: "application/ld+json" as const,
          innerHTML: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.value.map((it) => ({
              "@type": "Question",
              name: it.question,
              acceptedAnswer: { "@type": "Answer", text: it.answer },
            })),
          }),
        }]
      : []),
  ],
});

// Share URLs
const shareUrls = computed(() => {
  const encodedUrl = encodeURIComponent(canonicalUrl);
  const encodedTitle = encodeURIComponent(article.value?.title || "");
  return {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
  };
});

const categoryColors: Record<string, string> = {
  "guides-de-collecte": "bg-blue-100 text-blue-700",
  "conseils-fundraising": "bg-emerald-100 text-emerald-700",
  "fiscalite-juridique": "bg-amber-100 text-amber-700",
  "types-associations": "bg-purple-100 text-purple-700",
  "gestion-association": "bg-teal-100 text-teal-700",
  "fonctionnalites-donoor": "bg-orange-100 text-orange-700",
  "etudes-de-cas": "bg-pink-100 text-pink-700",
  actualites: "bg-sky-100 text-sky-700",
};

function getCategoryColor(catSlug: string) {
  return categoryColors[catSlug] || "bg-gray-100 text-gray-700";
}
</script>

<template>
  <div v-if="article" class="min-h-screen bg-white dark:bg-gray-950">
    <!-- Hero Section -->
    <div class="relative overflow-hidden">
      <!-- Background Image with Overlay -->
      <div class="absolute inset-0">
        <!-- srcset responsive : Directus assets redimensionne via ?width=.
             Sans lui, un mobile téléchargeait la variante 1400px (c'est
             l'image LCP de chaque article — 77+ pages concernées). -->
        <img
          :src="getImageUrl(article.image, 1400)"
          :srcset="`${getImageUrl(article.image, 640)} 640w, ${getImageUrl(article.image, 960)} 960w, ${getImageUrl(article.image, 1400)} 1400w`"
          sizes="100vw"
          :alt="article.title"
          :data-asset-id="article.image"
          class="w-full h-full object-cover"
          fetchpriority="high"
          @error="onImageError"
        />
        <div
          class="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"
        />
      </div>

      <!-- Content -->
      <UContainer class="relative py-20 md:py-28">
        <div class="max-w-4xl mx-auto text-center">
          <!-- Back Button -->
          <NuxtLink
            to="/blog"
            class="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <UIcon name="i-lucide-arrow-left" class="w-4 h-4" />
            <span>Retour au blog</span>
          </NuxtLink>

          <!-- Category Badge -->
          <div v-if="article.category" class="mb-6">
            <NuxtLink :to="`/blog/categorie/${article.category.slug}`">
              <UBadge
                :class="getCategoryColor(article.category.slug)"
                size="lg"
                class="font-semibold px-4 py-1.5"
              >
                {{ article.category.name }}
              </UBadge>
            </NuxtLink>
          </div>

          <!-- Title -->
          <h1
            class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight"
          >
            {{ article.title }}
          </h1>

          <!-- Meta Info -->
          <div
            class="flex flex-wrap items-center justify-center gap-6 text-white/80"
          >
            <!-- Author — lien vers la page auteur (E-E-A-T, audit SEO 2026-07-11) -->
            <NuxtLink
              v-if="article.author"
              :to="authorPagePath"
              class="flex items-center gap-3 group"
            >
              <img
                :src="getAvatarUrl(article.author.avatar)"
                :alt="article.author.name"
                width="100"
                height="100"
                class="w-12 h-12 rounded-full border-2 border-white/20"
              />
              <div class="text-left">
                <div class="font-semibold text-white group-hover:underline">
                  {{ article.author.name }}
                </div>
                <div class="text-sm text-white/60">
                  {{ article.author.role }}
                </div>
              </div>
            </NuxtLink>

            <div class="hidden sm:block w-px h-8 bg-white/20" />

            <!-- Date -->
            <div v-if="article.date_published" class="flex items-center gap-2">
              <UIcon name="i-lucide-calendar" class="w-4 h-4" />
              <span>{{ formatDate(article.date_published) }}</span>
            </div>

            <div class="hidden sm:block w-px h-8 bg-white/20" />

            <!-- Read Time -->
            <div v-if="article.read_time" class="flex items-center gap-2">
              <UIcon name="i-lucide-clock" class="w-4 h-4" />
              <span>{{ article.read_time }} de lecture</span>
            </div>
          </div>
        </div>
      </UContainer>
    </div>

    <!-- Main Content -->
    <UContainer class="py-12 md:py-16">
      <div class="max-w-7xl mx-auto">
        <div class="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <!-- Left Sidebar - Share Buttons (Desktop) -->
          <aside class="hidden lg:block lg:col-span-1">
            <div class="sticky top-24 space-y-4">
              <span
                class="text-xs font-semibold text-gray-500 uppercase tracking-wider"
              >
                Partager
              </span>
              <div class="flex flex-col gap-3">
                <a
                  :href="shareUrls.twitter"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-[#1DA1F2] hover:text-white transition-all"
                  title="Partager sur Twitter"
                >
                  <UIcon name="i-simple-icons-x" class="w-4 h-4" />
                </a>
                <a
                  :href="shareUrls.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-[#0A66C2] hover:text-white transition-all"
                  title="Partager sur LinkedIn"
                >
                  <UIcon name="i-simple-icons-linkedin" class="w-4 h-4" />
                </a>
                <a
                  :href="shareUrls.facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-[#1877F2] hover:text-white transition-all"
                  title="Partager sur Facebook"
                >
                  <UIcon name="i-simple-icons-facebook" class="w-4 h-4" />
                </a>
                <a
                  :href="shareUrls.whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-[#25D366] hover:text-white transition-all"
                  title="Partager sur WhatsApp"
                >
                  <UIcon name="i-simple-icons-whatsapp" class="w-4 h-4" />
                </a>
              </div>
            </div>
          </aside>

          <!-- Main Article Content -->
          <article class="lg:col-span-8 min-w-0">
            <template v-if="SEGMENTS_VIDEO.avant && videoArticle">
              <BlogArticleContent :html="SEGMENTS_VIDEO.avant.avant" />
              <BlogVideoFormation :video="videoArticle" />
              <BlogArticleContent :html="SEGMENTS_VIDEO.avant.apres" />
            </template>
            <BlogArticleContent v-else :html="POSITIONS_COUPE.avant" />

            <!-- Pont vers la page produit du besoin traité par l'article.
                 Rendu côté serveur, à une frontière de <h2> : la structure de
                 titres qui fait ranker l'article n'est pas modifiée. -->
            <BlogInlineCta
              v-if="POSITIONS_COUPE.apres"
              :slug="article?.slug"
              emplacement="milieu-article"
            />
            <template v-if="SEGMENTS_VIDEO.apres && !SEGMENTS_VIDEO.avant && videoArticle">
              <BlogArticleContent :html="SEGMENTS_VIDEO.apres.avant" />
              <BlogVideoFormation :video="videoArticle" />
              <BlogArticleContent :html="SEGMENTS_VIDEO.apres.apres" />
            </template>
            <BlogArticleContent v-else-if="POSITIONS_COUPE.apres" :html="POSITIONS_COUPE.apres" />

            <!-- Mobile Share Buttons -->
            <div
              class="lg:hidden mt-12 pt-8 border-t border-gray-200 dark:border-gray-800"
            >
              <h3 class="font-bold text-gray-900 dark:text-white mb-4">
                Partager cet article
              </h3>
              <div class="flex gap-3">
                <a
                  :href="shareUrls.twitter"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-[#1DA1F2] hover:text-white transition-all"
                >
                  <UIcon name="i-simple-icons-x" class="w-4 h-4" />
                  <span class="text-sm font-medium">Twitter</span>
                </a>
                <a
                  :href="shareUrls.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-[#0A66C2] hover:text-white transition-all"
                >
                  <UIcon name="i-simple-icons-linkedin" class="w-4 h-4" />
                  <span class="text-sm font-medium">LinkedIn</span>
                </a>
                <a
                  :href="shareUrls.whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-[#25D366] hover:text-white transition-all"
                >
                  <UIcon name="i-simple-icons-whatsapp" class="w-4 h-4" />
                  <span class="text-sm font-medium">WhatsApp</span>
                </a>
              </div>
            </div>

            <!-- Author Bio -->
            <div
              v-if="article.author"
              class="mt-12 p-6 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800"
            >
              <div class="flex items-start gap-4">
                <img
                  :src="getAvatarUrl(article.author.avatar)"
                  :alt="article.author.name"
                  class="w-16 h-16 rounded-full"
                />
                <div>
                  <h4 class="font-bold text-gray-900 dark:text-white mb-1">
                    {{ article.author.name }}
                  </h4>
                  <p class="text-sm text-(--color-main) font-medium mb-2">
                    {{ article.author.role }}
                  </p>
                  <p v-if="article.author.bio" class="text-sm text-gray-600 dark:text-gray-400">
                    {{ article.author.bio }}
                  </p>
                </div>
              </div>
            </div>

            <!-- CTA Donoor fin d'article -->
            <div
              class="mt-12 p-8 rounded-2xl bg-gradient-to-br from-(--color-main) to-(--color-main) text-white shadow-xl"
              style="background: linear-gradient(135deg, var(--color-main) 0%, var(--color-main-deep) 100%)"
            >
              <h3 class="text-2xl font-bold mb-3">
                Lancez votre collecte avec Donoor
              </h3>
              <p class="text-white/90 mb-5 leading-relaxed">
                Plateforme française de collecte de dons pour associations : 0 € d'abonnement,
                Tap to Pay iPhone, app mobile native, reçus fiscaux automatiques.
                Reversement direct sur votre compte (modèle non-custodial).
              </p>
              <div class="flex flex-wrap gap-3">
                <NuxtLink
                  to="/register"
                  v-register-cta="'fin-article'"
                  class="inline-flex items-center px-6 py-3 bg-white text-(--color-main) font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                  style="color: var(--color-main)"
                >
                  Créer un compte gratuit
                  <UIcon name="i-heroicons-arrow-right" class="ml-2 w-5 h-5" />
                </NuxtLink>
                <!-- ⚠️ /comparateurs/donoor-vs-helloasso n'existe PAS (404 sur les
                     56 articles, pire lien cassé du site — audit SEO 2026-07-10).
                     La page comparaison HelloAsso réelle est /alternative-helloasso. -->
                <!-- Second lien CONTEXTUEL au sujet de l'article, au lieu du
                     comparatif HelloAsso servi à l'identique sur les 147 articles.
                     Mesuré : le lecteur d'un article « organiser un loto » ne
                     cherche pas un comparatif de plateformes — il cherche comment
                     organiser son loto. La page produit du besoin convertit 14 à
                     29 %, contre 0 % pour l'article seul. -->
                <NuxtLink
                  :to="blogCtaTarget(article?.slug).to"
                  v-register-cta="`fin-article-contextuel:${blogCtaTarget(article?.slug).to}`"
                  class="inline-flex items-center px-6 py-3 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors"
                >
                  {{ blogCtaTarget(article?.slug).bouton }}
                </NuxtLink>
              </div>
            </div>
          </article>

          <!-- Right Sidebar - Table of Contents -->
          <aside class="hidden lg:block lg:col-span-3">
            <div
              v-if="tocLinks.length > 0"
              class="sticky top-24 p-6 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800"
            >
              <h3
                class="text-sm font-bold text-gray-900 dark:text-white mb-4 uppercase tracking-wider"
              >
                Sommaire
              </h3>
              <nav class="space-y-2">
                <button
                  v-for="link in tocLinks"
                  :key="link.id"
                  class="block w-full text-left text-sm transition-colors hover:text-primary"
                  :class="[
                    link.level === 3 ? 'pl-4' : '',
                    'text-gray-600 dark:text-gray-400',
                  ]"
                  @click="scrollToSection(link.id)"
                >
                  {{ link.text }}
                </button>
              </nav>
            </div>
          </aside>
        </div>
      </div>
    </UContainer>

    <!-- Related Articles -->
    <div
      v-if="relatedArticles.length > 0"
      class="bg-gray-50 dark:bg-gray-900 py-16"
    >
      <UContainer>
        <h2
          class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8"
        >
          Articles similaires
        </h2>
        <div class="grid md:grid-cols-3 gap-6">
          <NuxtLink
            v-for="relatedArticle in relatedArticles"
            :key="relatedArticle.id"
            :to="`/blog/${relatedArticle.slug}`"
            external
            class="group"
          >
            <UCard
              class="h-full rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <template #header>
                <img
                  :src="getImageUrl(relatedArticle.image, 400)"
                  :alt="relatedArticle.title"
                  :data-asset-id="relatedArticle.image"
                  class="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  @error="onImageError"
                />
              </template>
              <div class="space-y-3">
                <UBadge
                  v-if="relatedArticle.category"
                  :class="getCategoryColor(relatedArticle.category.slug)"
                  size="xs"
                >
                  {{ relatedArticle.category.name }}
                </UBadge>
                <h3
                  class="font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-main transition-colors"
                >
                  {{ relatedArticle.title }}
                </h3>
                <p
                  class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2"
                >
                  {{ relatedArticle.description }}
                </p>
              </div>
            </UCard>
          </NuxtLink>
        </div>
      </UContainer>
    </div>

    <!-- CTA Section -->
    <CtaBanner />
  </div>
</template>
